import React, { useEffect, useState } from "react";
import { Power, Play, Music2, Radio, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { FONT, SAFE_TOP, SIDE_DOCK_W, GLASS, glassBox, roundBtn } from "./ui";

const KEYFRAMES = `@keyframes jw-marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}@keyframes jw-blink{0%,100%{opacity:1}50%{opacity:.25}}@keyframes jw-pulse{0%{box-shadow:0 0 0 0 rgba(0,0,205,.45)}70%{box-shadow:0 0 0 6px rgba(0,0,205,0)}100%{box-shadow:0 0 0 0 rgba(0,0,205,0)}}`;

// Pil Radio Jawsel (kaca) — di bawah bar atas. Dua wujud:
//  • pil penuh: ON AIR + Radio | judul · artis, stasiun, baris berjalan kecil | tombol daya
//  • mini: tombol bulat ikon radio (titik berdenyut saat mengudara) — ketuk untuk membuka pil penuh.
// Ketuk blok kiri ("Radio ‹") untuk mengecilkan. Radio mengalir seperti siaran sungguhan (tanpa tombol lanjut).
// tampilMati=true (tempat publik tanpa dock radio, mis. Alun-alun): saat radio mati tetap tampil mini redup — ketuk untuk menyalakan.
export function RadioPlayer({ radio, mini, onMini, tampilMati = false, top = `calc(${SAFE_TOP} + 104px)`, right = SIDE_DOCK_W + 12 }) {
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(t); }, []);
  const hidup = radio.on && radio.now;
  if (!hidup && !tampilMati) return null;

  const wrap = { position: "absolute", left: 12, top, fontFamily: FONT, color: GLASS.ink, zIndex: 6,
    transform: `translateY(${shown ? 0 : -14}px)`, opacity: shown ? 1 : 0, transition: "transform .3s cubic-bezier(.2,.8,.2,1), opacity .3s" };

  const nyalakan = () => {
    if (radio.loaded && !radio.ready) return toast("Playlist radio masih kosong — admin belum mengunggah lagu");
    radio.toggle();
  };

  if (mini || !hidup) {
    return (
      <div data-testid="radio-player" style={wrap}>
        <style>{KEYFRAMES}</style>
        <button data-testid="radio-mini" aria-label={hidup ? "Buka radio" : "Nyalakan radio"} onClick={hidup ? () => onMini(false) : nyalakan}
          style={{ ...roundBtn(radio.playing), position: "relative", color: hidup ? GLASS.accent : GLASS.sub }}>
          <Radio size={18} strokeWidth={2} />
          {radio.playing && <span data-testid="radio-mini-dot" style={{ position: "absolute", top: 6, right: 6, width: 7, height: 7, borderRadius: 99, background: GLASS.accent, animation: "jw-pulse 1.6s ease-out infinite" }} />}
        </button>
      </div>
    );
  }

  const t = radio.now;
  const judul = `${t.title}${t.artist ? ` · ${t.artist}` : ""}`;
  const baris = radio.playing ? `Sedang mengudara: ${judul}` : "Ketuk tombol daya untuk melanjutkan siaran";
  const durasi = Math.max(26, Math.round(baris.length * 0.55));   // makin panjang teks makin lama → kecepatan baca tetap tenang

  return (
    <div data-testid="radio-player" role="region" aria-label="Radio Jawsel" style={{ ...wrap, right }}>
      <style>{KEYFRAMES}</style>
      <div style={{ display: "flex", alignItems: "stretch", gap: 8, padding: 6, ...glassBox, borderRadius: 20, maxWidth: 360 }}>
        {/* Blok kiri: ON AIR + Radio — ketuk untuk mengecilkan jadi ikon bulat */}
        <button data-testid="radio-minimize" aria-label="Kecilkan radio" onClick={() => onMini(true)}
          style={{ display: "flex", flexDirection: "column", gap: 3, flex: "none", width: 58, padding: 0, border: 0, background: "transparent", color: "inherit", fontFamily: FONT, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
          <span data-testid="radio-onair" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, height: 20, borderRadius: 8, background: radio.playing ? GLASS.accentBg : GLASS.inner, color: radio.playing ? GLASS.accent : GLASS.sub, fontSize: 9, fontWeight: 800, letterSpacing: 1 }}>
            <span style={{ width: 5, height: 5, borderRadius: 99, background: "currentColor", animation: radio.playing ? "jw-blink 1.2s ease-in-out infinite" : "none" }} />
            {radio.playing ? "ON AIR" : "JEDA"}
          </span>
          <span style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 1, borderRadius: 8, background: GLASS.innerOn, border: "1px solid var(--g-border)", fontSize: 10.5, fontWeight: 700, color: GLASS.ink }}>
            <ChevronLeft size={11} strokeWidth={2.4} style={{ marginLeft: -3 }} />Radio
          </span>
        </button>

        {/* Blok tengah: judul · artis, stasiun, baris berjalan kecil */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 2, WebkitTextSizeAdjust: "100%" }}>
          <span data-testid="radio-title" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, letterSpacing: -0.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            <Music2 size={11} style={{ flex: "none", color: GLASS.accent }} /><span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{judul}</span>
          </span>
          <span style={{ fontSize: 10, fontWeight: 500, color: GLASS.sub }}>Jawsel FM 99.9 · {radio.count} lagu</span>
          <div style={{ overflow: "hidden", whiteSpace: "nowrap", fontSize: 10, lineHeight: "13px", color: GLASS.accent, fontWeight: 600, WebkitTextSizeAdjust: "100%", maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}>
            <span data-testid="radio-marquee" style={{ display: "inline-block", fontSize: 10, animation: `jw-marquee ${durasi}s linear infinite` }}>
              {baris}<span style={{ display: "inline-block", width: 40 }} />{baris}<span style={{ display: "inline-block", width: 40 }} />
            </span>
          </div>
        </div>

        {/* Tombol daya: matikan radio / putar bila tertahan */}
        <button data-testid="radio-power" aria-label={radio.playing ? "Matikan radio" : "Putar radio"} onClick={radio.toggle}
          style={{ alignSelf: "center", width: 36, height: 36, borderRadius: 99, border: "1px solid var(--g-border)", background: radio.playing ? GLASS.innerOn : GLASS.accentBg, color: GLASS.accent, display: "grid", placeItems: "center", cursor: "pointer", flex: "none",
            boxShadow: "0 4px 12px rgba(20,20,20,0.06)", transition: "transform .12s", WebkitTapHighlightColor: "transparent" }}
          onPointerDown={e => { e.currentTarget.style.transform = "scale(.92)"; }}
          onPointerUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
          onPointerLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
          {radio.playing ? <Power size={15} strokeWidth={2.2} /> : <Play size={14} fill="currentColor" />}
        </button>
      </div>
    </div>
  );
}
